import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTaxYear } from "../contexts/TaxYearContext";
import {
  DollarSign, TrendingDown, TrendingUp, FileCheck2,
  Upload, Eye, Download, ArrowRight, Clock, RefreshCw, ChevronDown, ChevronRight, BookOpen
} from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import ApprovalFilterBanner, { KpiUnfilteredHint } from "../components/ApprovalFilterBanner";
import QuotaBell from "../components/QuotaBell";
import { getFriendlyUploadError } from "../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);

const EXPENSE_COLORS = ["#7C3AED", "#A78BFA", "#C4B5FD", "#F472B6", "#FB7185", "#FCD34D"];
const INCOME_COLORS = ["#2563EB", "#60A5FA", "#93C5FD", "#BFDBFE"];

const statusColor = { processed: "bg-emerald-50 text-emerald-700 border border-emerald-200", pending: "bg-amber-50 text-amber-700 border border-amber-200", processing: "bg-blue-50 text-blue-700 border border-blue-200", error: "bg-red-50 text-red-700 border border-red-200" };

export default function Dashboard() {
  const { user } = useAuth();
  const { taxYear, defaultTaxYear } = useTaxYear();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // "How it Works" panel — collapsible, default collapsed. State is local-only;
  // no backend, no localStorage, no progress tracking. Just open/close.
  const [howOpen, setHowOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, docRes] = await Promise.all([
        axios.get(`${API}/transactions/summary?tax_year=${taxYear}`, { withCredentials: true }),
        axios.get(`${API}/documents`, { withCredentials: true })
      ]);
      setSummary(sumRes.data);
      setDocs(docRes.data.slice(0, 5));
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [taxYear]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const expenseData = Object.entries(summary?.expense_breakdown || {})
    .map(([name, value]) => ({ name: name.replace("CACFP ", "").replace(" Expense", ""), value: Math.round(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 6);

  const incomeData = Object.entries(summary?.income_sources || {})
    .map(([name, value]) => ({ name: name.replace("CACFP ", "").replace(" Income", ""), value: Math.round(value) }))
    .sort((a, b) => b.value - a.value).slice(0, 6);

  const cacfpCategories = [
    { label: "CACFP Income", key: "CACFP Income", type: "income" },
    { label: "CACFP Food Expense", key: "CACFP Food Expense", type: "expense" },
    { label: "CACFP Non-Food Expense", key: "CACFP Non-Food Expense", type: "expense" },
    { label: "CACFP Utilities", key: "CACFP Utilities", type: "expense" },
    { label: "CACFP Use Charge for Space", key: "CACFP Use Charge for Space", type: "expense" },
    { label: "Maintenance Expense", key: "Maintenance Expense", type: "expense" },
    { label: "Co-Payments (Parent Fees)", key: "Co-Payments (Parent Fees)", type: "income" },
    { label: "Subsidy 1099 Income", key: "Subsidy 1099 Income", type: "income" },
  ];

  const getCategoryAmount = (key, type) => {
    if (type === "income") return summary?.income_sources?.[key] || 0;
    return summary?.expense_breakdown?.[key] || 0;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-80" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-slate-200 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => <div key={i} className="h-64 bg-slate-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Manrope" }}>
            <span data-testid="dashboard-welcome">Welcome, {user?.center_name || "Your Center"}</span>
          </h1>
          <p className="text-slate-500 text-sm mt-1">Here's a snapshot of your tax-prep progress.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh dashboard"
            data-testid="dashboard-refresh-btn"
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-slate-100 transition-all disabled:opacity-60"
          >
            <RefreshCw className={`w-5 h-5 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <QuotaBell />
          <img
            src="/assets/todays-kidz-logo.png"
            alt="ToDays Kidz — Simple Sign-In"
            data-testid="dashboard-logo"
            className="h-24 lg:h-32 w-auto object-contain hidden sm:block"
          />
        </div>
      </div>

      {/* How it Works — collapsible quick-orientation panel. Pure UI,
          no backend, no state persistence. Helps a first-time user
          understand the workflow in 5 plain-English steps. */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="how-it-works-panel">
        <button
          type="button"
          onClick={() => setHowOpen(o => !o)}
          aria-expanded={howOpen}
          data-testid="how-it-works-toggle"
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4.5 h-4.5 text-purple-700" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>How it Works</p>
              <p className="text-xs text-slate-500">A simple 5-step guide from upload to TurboTax</p>
            </div>
          </div>
          {howOpen ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </button>
        {howOpen && (
          <div className="px-5 pb-5 pt-1 border-t border-slate-100" data-testid="how-it-works-body">
            <ol className="space-y-3 mt-3">
              {[
                {
                  n: 1,
                  title: "Upload your documents",
                  body: `First, create a "Tax ${defaultTaxYear}" folder on your desktop and drop all your tax documents into it as they come in throughout the year — bank statements, receipts, CACFP reports, payroll, utility bills, the works. When you're ready, drag and drop or upload them onto the Upload & Extract page. PDFs and images go through AI OCR and get auto-categorized. CSV and Excel files skip AI — confirm the columns, save the layout, done.`,
                },
                {
                  n: 2,
                  title: "Review and approve transactions",
                  body: "The app auto-categorizes every line item into the right Schedule C or CACFP bucket. Open Transactions, fix any categories that look off, then click the green check to approve.",
                },
                {
                  n: 3,
                  title: "Set up CACFP allocation (one-time)",
                  body: "On the CACFP Overview page, enter your square footage to lock in your CACFP Space % (or your Time-Space % if you're home-based). The app uses this to split shared costs correctly.",
                },
                {
                  n: 4,
                  title: "Track equipment, vehicles, and grants",
                  body: "Add equipment over $200 (for Section 179 / depreciation), log vehicles with business-use %, and record any grants received. Reviewing once a quarter is enough.",
                },
                {
                  n: 5,
                  title: "Generate your Tax Prep document",
                  body: "When the year ends, run the Year-End wizard. It produces a TurboTax-ready Schedule C summary, Form 4562 (Section 179), and a full archive of every source PDF — ready to hand to your accountant or transcribe yourself.",
                },
              ].map(step => (
                <li key={step.n} className="flex gap-3" data-testid={`how-it-works-step-${step.n}`}>
                  <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {step.n}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>{step.title}</p>
                    <p className="text-slate-600 text-sm mt-0.5 leading-relaxed">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-slate-400 italic mt-4">
              Need more help? Open the Help Center from the sidebar for FAQs, document type lists, and tax-credit lookups.
            </p>
          </div>
        )}
      </div>

      {/* Upload CTA Banner */}
      {(summary?.total_transactions || 0) === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
          <div>
            <p className="font-semibold text-blue-900 text-sm" style={{ fontFamily: "Manrope" }}>No documents uploaded yet</p>
            <p className="text-blue-600 text-sm mt-1">Upload bank statements, CACFP reports, and 1099s to get started.</p>
          </div>
          <button onClick={() => navigate("/app/upload")} className="flex items-center gap-2 bg-[#2563EB] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1D4ED8] transition-all whitespace-nowrap">
            Upload Now <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stage 4 — approved-only filter banner */}
      <ApprovalFilterBanner summary={summary} />

      {/* Ship Gate 0a — dismissible CACFP "Application Pending" reminder */}
      <CacfpPendingReminder />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards-grid">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="kpi-total-income">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Income</span>
            <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-emerald-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmt(summary?.total_income)}</p>
          <p className="text-xs text-slate-400 mt-1">Year to Date</p>
          <KpiUnfilteredHint unfiltered={summary?.unfiltered_total_income} approved={summary?.total_income} testId="kpi-income-unfiltered-hint" />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="kpi-total-expenses">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Expenses</span>
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-red-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmt(summary?.total_expenses)}</p>
          <p className="text-xs text-slate-400 mt-1">Year to Date</p>
          <KpiUnfilteredHint unfiltered={summary?.unfiltered_total_expenses} approved={summary?.total_expenses} testId="kpi-expenses-unfiltered-hint" />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="kpi-net-income">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Net Income</span>
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-500" />
            </div>
          </div>
          <p className={`text-3xl font-bold ${(summary?.net_income || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`} style={{ fontFamily: "Manrope" }}>
            {fmt(summary?.net_income)}
          </p>
          <p className="text-xs text-slate-400 mt-1">Year to Date</p>
          <KpiUnfilteredHint unfiltered={summary?.unfiltered_net_income} approved={summary?.net_income} testId="kpi-net-unfiltered-hint" />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="kpi-documents-processed">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Docs Processed</span>
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <FileCheck2 className="w-4 h-4 text-purple-500" />
            </div>
          </div>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary?.documents_processed || 0}</p>
          <p className="text-xs text-slate-400 mt-1">This Year</p>
        </div>
      </div>

      {/* Middle Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* CACFP Summary */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="cacfp-summary-table">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>CACFP Summary Overview</h2>
            <Link to="/app/cacfp" className="text-[#2563EB] text-sm font-medium hover:underline" data-testid="cacfp-view-full-report">View Full Report</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">YTD Amount</th>
                </tr>
              </thead>
              <tbody>
                {cacfpCategories.map(({ label, key, type }) => {
                  const amt = getCategoryAmount(key, type);
                  if (amt === 0) return null;
                  return (
                    <tr key={key} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-3 text-slate-700 font-medium">{label}</td>
                      <td className={`px-6 py-3 text-right font-semibold ${type === "income" ? "text-emerald-600" : "text-red-600"}`}>
                        {type === "expense" ? "-" : ""}{fmt(amt)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                  <td className="px-6 py-3 text-slate-900">Net Income</td>
                  <td className={`px-6 py-3 text-right text-lg ${(summary?.net_income || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {fmt(summary?.net_income)}
                  </td>
                </tr>
              </tbody>
            </table>
            {!summary?.total_transactions && (
              <div className="text-center py-8 text-slate-400 text-sm">No transactions yet. Upload documents to get started.</div>
            )}
          </div>
        </div>

        {/* Recent Uploads + Extraction Summary */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="recent-uploads-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Recent Uploads</h2>
              <Link to="/app/reports" className="text-[#2563EB] text-xs font-medium hover:underline">View All</Link>
            </div>
            <div className="p-3">
              {docs.length === 0 ? (
                <p className="text-slate-400 text-xs text-center py-6">No uploads yet</p>
              ) : docs.map(doc => (
                <div key={doc._id} className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="w-7 h-7 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0">
                    <FileCheck2 className="w-3.5 h-3.5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700 text-xs font-medium truncate">{doc.original_name}</p>
                    <p className="text-slate-400 text-xs">{doc.document_type}</p>
                    {doc.processing_status === "error" && (
                      <p className="text-red-500 text-xs mt-0.5" data-testid={`dashboard-doc-error-${doc._id}`}>
                        {getFriendlyUploadError(doc.error_message)}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${statusColor[doc.processing_status] || statusColor.pending}`}>
                    {doc.processing_status}
                  </span>
                </div>
              ))}
              <button onClick={() => navigate("/app/upload")}
                className="w-full text-[#2563EB] text-xs font-medium mt-2 py-2 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                data-testid="dashboard-upload-more-btn">
                <Upload className="w-3.5 h-3.5" /> Upload More Documents
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="extraction-summary-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Extraction Summary</h2>
              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">This Year</span>
            </div>
            <div className="space-y-2">
              {[
                { label: "Transactions Extracted", value: summary?.total_transactions || 0, color: "text-slate-900" },
                { label: "Categorized", value: summary?.categorized || 0, color: "text-emerald-600" },
                { label: "Needs Review", value: summary?.needs_review || 0, color: "text-amber-600" },
                { label: "Total Documents", value: summary?.total_documents || 0, color: "text-blue-600" },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex justify-between items-center py-1">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className={`text-sm font-bold ${color}`}>{value.toLocaleString()}</span>
                </div>
              ))}
            </div>
            {(summary?.needs_review || 0) > 0 && (
              <button onClick={() => navigate("/app/transactions")}
                className="w-full mt-3 text-xs text-[#2563EB] font-medium flex items-center justify-center gap-1 hover:bg-blue-50 py-1.5 rounded-lg transition-colors"
                data-testid="review-transactions-btn">
                <Eye className="w-3.5 h-3.5" /> Review Transactions
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Donut */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="expense-breakdown-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>CACFP Expense Breakdown</h2>
            <Link to="/app/expenses" className="text-[#2563EB] text-sm font-medium hover:underline">View All</Link>
          </div>
          {expenseData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={expenseData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" paddingAngle={2}>
                  {expenseData.map((_, i) => <Cell key={i} fill={EXPENSE_COLORS[i % EXPENSE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val) => fmt(val)} />
                <Legend iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-slate-300 text-sm">No expense data yet</div>
          )}
        </div>

        {/* Income Bar */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="income-sources-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Income Sources</h2>
            <Link to="/app/income" className="text-[#2563EB] text-sm font-medium hover:underline">View All</Link>
          </div>
          {incomeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={incomeData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(val) => fmt(val)} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {incomeData.map((_, i) => <Cell key={i} fill={INCOME_COLORS[i % INCOME_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-slate-300 text-sm">No income data yet</div>
          )}
        </div>
      </div>

      {/* TurboTax Export */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="turbotax-export-section">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-slate-900 text-base mb-1" style={{ fontFamily: "Manrope" }}>Export for TurboTax</h2>
            <p className="text-slate-500 text-sm mb-3">Generate a clean, categorized report ready to import into TurboTax.</p>
            <div className="grid grid-cols-2 gap-2">
              {["Income & Expense Summary", "CACFP Categories", "Transaction Details", "TurboTax Format"].map(item => (
                <div key={item} className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                  </div>
                  <span className="text-slate-600 text-xs">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={() => navigate("/app/turbotax")}
            data-testid="generate-tax-document-btn"
            className="flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold px-6 py-3 rounded-xl transition-all whitespace-nowrap"
          >
            <Download className="w-4 h-4" /> Generate Tax Document
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-actions-grid">
        {[
          { icon: Upload, label: "Upload Documents", desc: "Add bank statements, reports & more", path: "/app/upload" },
          { icon: Eye, label: "Review Transactions", desc: "Check and edit extracted items", path: "/app/transactions" },
          { icon: Clock, label: "Manage Categories", desc: "Customize CACFP categories", path: "/app/transactions" },
          { icon: Download, label: "Download Reports", desc: "Export reports & summaries", path: "/app/turbotax" },
        ].map(({ icon: Icon, label, desc, path }) => (
          <button
            key={label}
            onClick={() => navigate(path)}
            data-testid={`quick-action-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-[#2563EB] hover:shadow-md transition-all group"
          >
            <div className="w-9 h-9 bg-slate-100 group-hover:bg-blue-50 rounded-lg flex items-center justify-center mb-3 transition-colors">
              <Icon className="w-4 h-4 text-slate-500 group-hover:text-[#2563EB] transition-colors" />
            </div>
            <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>{label}</p>
            <p className="text-slate-400 text-xs mt-0.5">{desc}</p>
          </button>
        ))}
      </div>

      {/* Pro Tip */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 flex items-center gap-3">
        <Clock className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <p className="text-blue-700 text-sm"><strong>Pro Tip:</strong> Uploading statements regularly helps ensure accurate categorization and saves time at tax season!</p>
      </div>
    </div>
  );
}

// ── CACFP "Application Pending" dismissible reminder (Ship Gate 0a) ─────────
function CacfpPendingReminder() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  // Show only when the user marked themselves as "pending" during the wizard.
  if (!user?.cacfp_application_pending || dismissed) return null;
  return (
    <div
      data-testid="dashboard-cacfp-pending-card"
      className="bg-amber-50 border-2 border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3"
    >
      <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-bold text-amber-900" style={{ fontFamily: "Manrope" }}>
          CACFP application pending
        </p>
        <p className="text-xs text-amber-800 mt-1 leading-relaxed">
          Once approved, enable CACFP in <a href="/app/settings?tab=cacfp" className="underline font-bold">Settings → CACFP</a> to unlock the food-program calculators and merge them into your Tax Prep.
        </p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        data-testid="dashboard-cacfp-pending-dismiss"
        className="text-amber-700 hover:text-amber-900 text-xs font-bold px-2 py-1"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
